import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@core/i18n';
import * as Sentry from '@sentry/react';
import { useBookingFlow, GUARANTEE_PATH, TIME_PATH } from '@features/booking/flow';
import { useBooking, CARD_FIELDS_RESET } from '@features/booking/state';
import { useRestaurant, TENANT_QUERY_KEY } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth';
import {
  useAnalytics,
  deriveSourceStepFromPath,
  categorizeCommitError,
  safeErrorCode,
  makeCommitAttemptEvent,
  makeCommitFailureEvent,
} from '@core/analytics';
import type { CommitFunnelAttribution } from '@core/analytics';
import { commitErrorKey } from '@features/booking/utils/commitMomentCopy';
import { ThreeDsError } from '@features/booking/hooks/handle3dsChallenge';
import { timeSlotsKeys } from '@features/booking/hooks/useTimeSlots';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { commitReservation } from '@features/booking/services/commitReservation';
import { CommitError, MissingReservationIdError, TIP_STALE_CODES } from '@features/booking/services/commit';
import { ApiError } from '@core/api/apiClient';

/**
 * Result shape returned to the page. The hook owns React state for the
 * commit lifecycle; the page reads `commit`, renders the button against
 * `isSubmitting`, displays `error`, and shows the reload prompt when
 * `requireReload` is true.
 */
export interface UseReservationCommitResult {
  /** Invoke once per user click. Idempotent against double-fire only via React's setIsSubmitting button-disable. */
  commit: () => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  /** MP-only: true after Stripe.js v3 loaded during 3DS. Page must show reload-to-retry UX. */
  requireReload: boolean;
  /**
   * True while a 3DS challenge is active (between path-module on3dsStart and
   * on3dsEnd). Pages render <ThreeDSChallengeOverlay open={isHandling3ds} />
   * against this flag — see T30 / D2 in the plan.
   */
  isHandling3ds: boolean;
}

/**
 * Backend business rejections that fire BEFORE the processor charge runs
 * (GrabController returns these ahead of the ProcessPayment step). The card
 * token was never sent to the processor, so it is still valid — don't force
 * the user back through the card form for an error a new card can't fix.
 * Mirrors the `backendErrorMessages` mapping table inside the hook.
 */
const PRE_PAYMENT_BACKEND_CODES = new Set<string>([
  'RESTAURANT_INACTIVE',
  'BLOCKED_USER_SUBTITLE',
  'MONTHLY_LIMIT_REACHED',
]);

/**
 * Decide whether a failed commit must drop the tokenized card from booking
 * state so the GUARANTEE step re-enters the flow and the next attempt
 * re-tokenizes.
 *
 * Card tokens are single-use at both processors: once the backend attempts a
 * charge, the token is consumed even when the charge is DECLINED. Keeping the
 * consumed token in state after a decline caused the "Invalid card_token_id"
 * retry cascade seen in the payment journal (casalascujas, 2026-08): with
 * `hasCardToken` true, `isGuaranteeRequired` filters the payment step out of
 * the flow, so the ConfirmationPage retry button re-POSTs the dead token on
 * every click — one journal row per click, no way to re-enter the card
 * without a full page reload.
 *
 * Exceptions — cases where re-tokenizing is wrong or unnecessary:
 * - ThreeDsError PAYMENT_3DS_CONFIRM_ERROR: the card WAS charged and only the
 *   confirm API failed. Routing the user back to the card form invites a
 *   double charge; the error copy already says "don't retry".
 * - MissingReservationIdError: 200 without reservationId — payment succeeded,
 *   the response is malformed. Same double-charge risk as above.
 * - Network/timeout errors: the request may never have reached the backend,
 *   so the token is likely still usable and an immediate retry should just
 *   work. If the charge WAS attempted, the retry fails as a non-network
 *   error and gets cleared then — the cascade is capped at one extra
 *   attempt instead of unbounded.
 * - Pre-payment backend rejections (see PRE_PAYMENT_BACKEND_CODES).
 *
 * Exported for the regression tests in useReservationCommit.test.tsx.
 */
export function shouldClearCardTokenAfterCommitFailure(cause: unknown): boolean {
  if (cause instanceof ThreeDsError && cause.code === 'PAYMENT_3DS_CONFIRM_ERROR') {
    return false;
  }
  if (cause instanceof MissingReservationIdError) return false;
  if (cause instanceof ApiError && cause.isNetworkError()) return false;
  if (cause instanceof Error && PRE_PAYMENT_BACKEND_CODES.has(cause.message)) {
    return false;
  }
  return true;
}

/**
 * React hook that wraps commitReservation. Pulls all the providers/context it
 * needs internally so the calling page just receives `{ commit, isSubmitting,
 * error, requireReload }`.
 *
 * The hook is "thin" — it owns React state (the three useState calls) and
 * error-to-UI mapping. The reservation logic itself lives in the framework-
 * free commitReservation function.
 *
 * isSubmitting deferral: the original handleConfirm only flipped
 * isSubmitting AFTER guards passed. The hook mirrors this by passing an
 * onCommitStart callback to commitReservation; it fires after guards pass
 * but before the first await. So guard rejections never flicker the button.
 *
 * Telemetry (T26): fires BOOKING_COMMIT_ATTEMPT on every click before guards
 * run, and BOOKING_COMMIT_FAILURE in the catch block. Both carry a
 * `source_step` attribute derived from the URL pathname so the same metric
 * works pre- and post-flag-flip (confirm | guarantee | personalize).
 */
export function useReservationCommit(): UseReservationCommitResult {
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const { dateLocale, currentLanguage } = useLanguage();
  const { goToNext, goToStep } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { trackConversion, trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { feeBreakdown } = usePaymentFee();
  const location = useLocation();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MP-only: set after a ThreeDsError. Stripe.js v3 loads lazily inside
  // handle3dsChallenge and overwrites window.Stripe, killing the v2 instance
  // the MP form used to tokenize. Retry requires a fresh page load.
  const [requireReload, setRequireReload] = useState(false);
  // T30: flipped true by the commit path module when handle3dsChallenge is
  // about to fire; flipped back in the path's finally. The page renders
  // <ThreeDSChallengeOverlay open={isHandling3ds} /> against this.
  const [isHandling3ds, setIsHandling3ds] = useState(false);

  // T26 telemetry: track when the hook first mounted on this step so we can
  // compute time_on_step_ms (mount → commit click). useRef ensures we only
  // capture the first-mount timestamp, not every re-render.
  const mountedAtRef = useRef<number>(Date.now());

  const invalidateTimeSlots = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: timeSlotsKeys.all });
  }, [queryClient]);

  const commit = useCallback(async () => {
    setError(null);

    // Build the funnel-event attribution that BOTH commit_attempt and
    // commit_failure events share. Computed once per click so the timing is
    // pinned at the moment the user pressed the button.
    const attribution: CommitFunnelAttribution = {
      source_step: deriveSourceStepFromPath(location.pathname),
      guarantee_type: booking.isEarlyAccessFlow ? 'early_access' : booking.guaranteeType,
      payment_processor: booking.paymentProcessor ?? 'none',
      is_waiting_list: booking.flowType === 'waiting_list',
      time_on_step_ms: Date.now() - mountedAtRef.current,
    };

    // Fire the attempt event BEFORE the API call. Guards in commitReservation
    // may short-circuit (return 'guarded') — but the user did click, so the
    // attempt should land regardless. The dashboard separates "clicked, didn't
    // succeed" from "clicked, succeeded" via the success event firing or not.
    //
    // Local try/catch protects against a broken trackEvent contract (e.g., a
    // partially-mocked provider, a content-blocker-shim that throws). The
    // AnalyticsProvider already hardens window.gtag calls with safeCall, but
    // the hook's invariant — "the user sees the booking outcome regardless of
    // analytics state" — is too important to depend on the provider alone.
    try {
      trackEvent(makeCommitAttemptEvent(attribution));
    } catch {
      // Analytics is observational. Never break the booking on its account.
    }

    try {
      await commitReservation(
        {
          booking,
          restaurant,
          authState,
          feeBreakdown,
          currentLanguage,
          dateLocale,
          trackConversion,
          invalidateTimeSlots,
          goToNext,
          goToStep,
          // T30 overlay: piped down so depositStripe/depositMercadoPago can
          // flip the UI flag around their handle3dsChallenge call.
          on3dsStart: () => setIsHandling3ds(true),
          on3dsEnd: () => setIsHandling3ds(false),
        },
        // Fires after guards pass, before any await. This is when the button
        // should disable. Guard rejections don't call this, so no flicker.
        () => setIsSubmitting(true),
      );
    } catch (err) {
      const cause = err instanceof CommitError ? err.cause : err;
      const mpFlow3dsLoaded = err instanceof CommitError ? err.mpFlow3dsLoaded : false;

      // MP-only reload guard: if this submit loaded Stripe.js v3 (via
      // handle3dsChallenge with requires_action=true), v2 is dead globally
      // and any retry without page reload would silently produce a bad
      // token. Set the reload UX regardless of error type. Stripe-primary
      // ThreeDsErrors don't trigger this — v3 was already loaded by
      // Elements before submit, no v2 to break.
      if (mpFlow3dsLoaded) {
        setRequireReload(true);
      }

      // T26 telemetry: fire the failure event with a coarse category. Sentry
      // already captures full error context via capturePaymentError inside
      // commitReservation; this event is for funnel monitoring only.
      // Wrapped in try/catch so a broken trackEvent never suppresses the
      // setError call below — the user-visible error message is the contract.
      const causeAsError = cause instanceof Error ? cause : null;
      try {
        trackEvent(
          makeCommitFailureEvent(attribution, {
            error_category: categorizeCommitError({
              causeName: causeAsError?.name,
              causeMessage: causeAsError?.message ?? '',
            }),
            error_code: safeErrorCode(cause),
          }),
        );
      } catch {
        // Analytics is observational. Never break the booking on its account.
      }

      // Error → user-facing copy. Three precedence rules:
      //
      // 1. ThreeDsError → cause.code as the i18n key (errors namespace).
      //    The codes are discrete and CRITICAL to keep distinct:
      //    - PAYMENT_3DS_CANCELLED  → user cancelled the bank challenge
      //    - PAYMENT_3DS_FAILED     → bank refused verification
      //    - PAYMENT_3DS_CONFIRM_ERROR → CARD WAS CHARGED but confirm API
      //      failed — user must NOT retry (would double-charge) and should
      //      contact the restaurant. Collapsing this to a generic "try
      //      again" message would invite the double-charge scenario.
      //    See handle3dsChallenge.ts for the ThreeDsErrorCode union.
      //
      // 2. Backend codes (RESTAURANT_INACTIVE, BLOCKED_USER, MONTHLY_LIMIT)
      //    keep their existing per-code messages — most precise copy.
      //
      // 3. Anything else falls through to the category-based commit error
      //    key (T30 D4): 3DS rejected (non-ThreeDsError 3DS errors), card
      //    declined, network, generic.
      let userErrorMessage: string;
      if (cause instanceof ThreeDsError) {
        userErrorMessage = tErrors(cause.code);
      } else {
        const rawMessage = cause instanceof Error ? cause.message : '';

        // See TIP_STALE_CODES (services/commit/shared.ts) for why both codes
        // land here. Every commit host shares this hook, so handling it once
        // covers PERSONALIZE, GUARANTEE, and CONFIRMATION — the platform
        // equivalent of this check missed a host by handling it per-page
        // instead. Card token is untouched: the processor was never reached
        // (unconditional early `return` below skips the CARD_FIELDS_RESET
        // block entirely — it never even consults PRE_PAYMENT_BACKEND_CODES).
        // Slots and the guest's tip pick are stale too, so both are dropped
        // and the guest is sent back to re-pick a time (and, with it, fresh
        // tip options).
        //
        // The copy is ALSO persisted to `booking.tipPolicyRefreshMessage`
        // (not just the local `error` state below) because `navigate`
        // unmounts this commit-host page in the same tick — SelectTimePage
        // reads the persisted copy after the redirect. Mirrors the
        // `paymentErrorMessage` carry-over pattern further down this file.
        //
        // The REJECTION ITSELF means an admin just edited this venue's tip
        // config server-side — but useRestaurant() is backed by a 5-minute
        // staleTime React Query cache (TENANT_QUERY_KEY) that nothing else
        // invalidates on this path. Without this, resolveTip() on the next
        // TIME render would resolve from the SAME stale
        // restaurant.specificServiceChargeOptions/allowGratuity that
        // produced the rejection, re-offering the withdrawn rate and
        // reproducing the 400 on retry (PR #476 review).
        if (TIP_STALE_CODES.has(rawMessage)) {
          invalidateTimeSlots();
          queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEY });
          booking.setTimeSlots(null);
          booking.updateBooking({
            tipChargePercent: null,
            // Cleared with tipChargePercent, as PAYMENT_STATE_RESET does.
            // The payload's `gratuity` comes from the live fee breakdown
            // (buildBasePayload), so it always matches tipChargePercent.
            feeGratuity: 0,
            tipPolicyRefreshMessage: t('NEW_MODAL_TIP_POLICY_REFRESH'),
          });
          setError(t('NEW_MODAL_TIP_POLICY_REFRESH'));
          navigate(TIME_PATH);
          return;
        }

        const backendErrorMessages: Record<string, string> = {
          RESTAURANT_INACTIVE: tErrors('GENERAL_SERVER_ERROR'),
          BLOCKED_USER_SUBTITLE: t('BLOCKED_USER_SUBTITLE'),
          MONTHLY_LIMIT_REACHED: t('CONFIRMATION_MONTHLY_LIMIT'),
        };
        const backendCopy = backendErrorMessages[rawMessage];
        if (backendCopy) {
          userErrorMessage = backendCopy;
        } else {
          const category = categorizeCommitError({
            causeName: cause instanceof Error ? cause.name : undefined,
            causeMessage: rawMessage,
          });
          userErrorMessage = tErrors(commitErrorKey(category));
        }
      }
      setError(userErrorMessage);

      // Single-use token invalidation: a charge attempt consumes the card
      // token even on decline. Drop it (and everything the card form will
      // re-populate) so `isGuaranteeRequired` turns true again and the retry
      // re-tokenizes instead of re-POSTing the dead token ("Invalid
      // card_token_id" cascade). See shouldClearCardTokenAfterCommitFailure
      // for the exception list. CARD_FIELDS_RESET on purpose does NOT touch
      // `paymentProcessor` (funnel attribution for the recovery click) nor
      // `guaranteeType`/`feeDeposit` (the payment guard keys off them).
      //
      // The error copy rides along as `paymentErrorMessage` so the payment
      // form can tell the guest WHY they're re-entering their card, and we
      // route there immediately instead of leaving them to discover the
      // redirect on their next click. Direct `navigate` on purpose:
      // `goToStep('GUARANTEE')` reads `activeSteps` from the CURRENT render,
      // where `hasCardToken` is still true and GUARANTEE is filtered out, so
      // it would silently no-op (same render-snapshot trap the payment forms
      // document around their flushSync blocks).
      //
      // Two cases keep the user where they are:
      // - mpFlow3dsLoaded: Stripe.js v3 replaced the v2 SDK this tab needs
      //   for MP tokenization — the form CAN'T mint a fresh token until the
      //   page reloads, and the reload prompt lives on this page.
      // - already on /payment (the form is the commit host): the form shows
      //   the error itself, fields still filled, next submit re-tokenizes.
      if (booking.cardToken && shouldClearCardTokenAfterCommitFailure(cause)) {
        booking.updateBooking({
          ...CARD_FIELDS_RESET,
          paymentErrorMessage: userErrorMessage,
        });
        if (!mpFlow3dsLoaded && location.pathname !== GUARANTEE_PATH) {
          // Same shape as useBookingFlow's emitStepBreadcrumb so this jump
          // shows up in the booking-flow trail — it's the one transition
          // that bypasses goToStep, and the one a payment-journal forensic
          // needs to link the failed commit to the fresh tokenization.
          Sentry.addBreadcrumb({
            category: 'booking-flow',
            message: 'jump -> GUARANTEE',
            data: {
              direction: 'jump',
              toStep: 'GUARANTEE',
              toPath: GUARANTEE_PATH,
              reason: 'card_token_invalidated',
            },
            level: 'info',
          });
          navigate(GUARANTEE_PATH);
        }
      }
    } finally {
      setIsSubmitting(false);
      // Belt-and-suspenders: even if a path module forgot to call on3dsEnd
      // (e.g., a future refactor or a thrown error before the finally), force
      // the overlay closed at the end of every commit. Without this, a stuck
      // overlay could trap the user behind an invisible modal.
      setIsHandling3ds(false);
    }
  }, [
    booking,
    restaurant,
    authState,
    feeBreakdown,
    currentLanguage,
    dateLocale,
    trackConversion,
    trackEvent,
    invalidateTimeSlots,
    queryClient,
    goToNext,
    goToStep,
    location.pathname,
    navigate,
    t,
    tErrors,
  ]);

  return { commit, isSubmitting, error, requireReload, isHandling3ds };
}
